import type { PaymentPageData } from "@/lib/api/payment-page.schema";
import type { Cart } from "@/hooks/use-cart";
import {
  formatAmount,
  formatDisplayAmount,
} from "@/app/[lang]/checkout/[token]/_components/payment.utils";
import type { MarketPreview } from "@/lib/markets";

/**
 * Currency the customer is quoted in before checkout. Catalog items carry
 * their own currency (which may differ from the page's), so a catalog page
 * quotes in the cart's currency; fixed pages quote in the page currency.
 */
export function quotedCurrency(pageData: PaymentPageData, cart: Cart): string {
  return pageData.page_type === "catalog"
    ? (cart.currency ?? pageData.currency)
    : pageData.currency;
}

/**
 * Informational secondary currency the page asks us to render page-currency
 * amounts in, with the rate to convert them. Null when not applicable.
 */
export function getDisplayConversion(
  pageData: PaymentPageData,
): { currency: string; rate: number } | null {
  const currency =
    pageData.display_currency &&
    pageData.display_currency !== pageData.currency
      ? pageData.display_currency
      : null;
  const rate =
    pageData.display?.rate ??
    pageData.items?.find((it) => it.display)?.display?.rate ??
    null;
  return currency && rate !== null ? { currency, rate } : null;
}

/**
 * Formatter for amounts quoted on a page. Page-currency amounts are shown in
 * the previewed market's currency when a preview conversion exists, else
 * honour the merchant's display-currency conversion. Amounts in any other
 * currency (a cart of foreign-priced items) are shown as-is, since neither
 * conversion applies to them.
 *
 * The returned function takes the amount and, optionally, the currency it is
 * in; it defaults to the currency the page is quoting in.
 */
export function createPageAmountFormatter(
  pageData: PaymentPageData,
  cart: Cart,
  preview?: MarketPreview | null,
): (amount: number, currency?: string) => string {
  const quoted = quotedCurrency(pageData, cart);
  const view =
    preview && preview.market.currency !== pageData.currency ? preview : null;
  const display = getDisplayConversion(pageData);

  return (amount, currency = quoted) => {
    if (currency !== pageData.currency) return formatAmount(amount, currency);
    if (view) return formatAmount(amount * view.rate, view.market.currency);
    if (display) return formatDisplayAmount(amount * display.rate, display.currency);
    return formatAmount(amount, currency);
  };
}
